import { useEffect, useState } from 'react';
import { X, Plus, Code } from 'lucide-react';
import { toneColor } from '../shared';
import {
  IconButton,
  Button,
  Input,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
} from '@vnetwork/ui/primitives';
import {
  FIELD_GROUPS,
  operatorsForField,
  HTTP_METHODS,
  COUNTRY_CODES,
  placeholderForField,
  parseWirefilter,
  serializeWirefilter,
  cbId,
  type ConditionGroup,
} from './wirefilter';

interface ConditionBuilderProps {
  value: string;
  onChange: (value: string) => void;
  /** Called when the expression can't be represented visually → caller flips to raw mode. */
  onParseFail: () => void;
}

const newRule = () => ({ id: cbId(), field: 'http.uri.path', operator: '==', value: '' });

/**
 * Visual wirefilter condition builder (When / AND / OR groups) — the new-design
 * re-skin of the legacy SwiftFederation ConditionBuilder. Logic lives in
 * `wirefilter.ts`; this is the presentation.
 */
export function ConditionBuilder({ value, onChange, onParseFail }: ConditionBuilderProps) {
  const [groups, setGroups] = useState<ConditionGroup[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const parsed = parseWirefilter(value);
    if (parsed === null) {
      onParseFail();
    } else {
      setGroups(parsed.length ? parsed : [{ id: cbId(), rules: [newRule()] }]);
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commit = (next: ConditionGroup[]) => {
    setGroups(next);
    onChange(serializeWirefilter(next));
  };

  const addGroup = () => commit([...groups, { id: cbId(), rules: [newRule()] }]);
  const addRule = (gId: string) =>
    commit(groups.map((g) => (g.id === gId ? { ...g, rules: [...g.rules, newRule()] } : g)));
  const removeRule = (gId: string, rId: string) =>
    commit(
      groups
        .map((g) => (g.id === gId ? { ...g, rules: g.rules.filter((r) => r.id !== rId) } : g))
        .filter((g) => g.rules.length > 0),
    );
  const updateRule = (gId: string, rId: string, key: 'field' | 'operator' | 'value', val: string) =>
    commit(
      groups.map((g) => {
        if (g.id !== gId) return g;
        return {
          ...g,
          rules: g.rules.map((r) => {
            if (r.id !== rId) return r;
            const nr = { ...r, [key]: val };
            if (key === 'field') {
              if (val === 'ssl') { nr.operator = 'is_true'; nr.value = ''; }
              else {
                if (r.field === 'ssl') nr.operator = '==';
                // Drop an operator the new field's type can't use (e.g. switching
                // a string field set to "matches" over to an IP/number field).
                if (!operatorsForField(val).some((o) => o.value === nr.operator)) nr.operator = '==';
                if (val === 'http.method' || val === 'http.geo.country') nr.value = '';
              }
            }
            return nr;
          }),
        };
      }),
    );

  if (!ready) return null;
  const preview = serializeWirefilter(groups);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
      {groups.map((group, gIdx) => (
        <div
          key={group.id}
          style={{
            position: 'relative',
            border: '1px solid var(--vn-border-default)',
            borderRadius: 'var(--vn-radius-md)',
            padding: 'var(--vn-space-4)',
            background: 'var(--vn-bg-surface)',
          }}
        >
          {gIdx > 0 && (
            <span
              style={{
                position: 'absolute',
                top: -10,
                left: 14,
                fontSize: 11,
                fontWeight: 700,
                color: toneColor('info', 'text'),
                background: 'var(--vn-bg-surface)',
                padding: '0 6px',
              }}
            >
              OR
            </span>
          )}
          <div style={{ display: 'grid', gap: 10 }}>
            {group.rules.map((rule, rIdx) => (
              <div key={rule.id} style={{ display: 'flex', gap: 'var(--vn-space-2)', alignItems: 'center' }}>
                <span style={{ width: 36, fontSize: 12, fontWeight: 700, color: 'var(--vn-fg-muted)' }}>
                  {rIdx === 0 ? 'When' : 'AND'}
                </span>
                <Select value={rule.field} onValueChange={(v) => updateRule(group.id, rule.id, 'field', v)}>
                  <SelectTrigger style={{ flex: 1, minWidth: 150 }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_GROUPS.map((fg) => (
                      <SelectGroup key={fg.group}>
                        <SelectLabel>{fg.group}</SelectLabel>
                        {fg.items.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>

                {rule.field === 'ssl' ? (
                  <Select value={rule.operator} onValueChange={(v) => updateRule(group.id, rule.id, 'operator', v)}>
                    <SelectTrigger style={{ flex: 0.8, minWidth: 120 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="is_true">is true</SelectItem>
                      <SelectItem value="is_false">is false</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Select value={rule.operator} onValueChange={(v) => updateRule(group.id, rule.id, 'operator', v)}>
                    <SelectTrigger style={{ flex: 0.8, minWidth: 120 }}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {operatorsForField(rule.field).map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {rule.field !== 'ssl' && (
                  <div style={{ flex: 2 }}>
                    {rule.field === 'http.method' ? (
                      <Select value={rule.value} onValueChange={(v) => updateRule(group.id, rule.id, 'value', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          {HTTP_METHODS.map((m) => (
                            <SelectItem key={m} value={m}>
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : rule.field === 'http.geo.country' ? (
                      <Select value={rule.value} onValueChange={(v) => updateRule(group.id, rule.id, 'value', v)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_CODES.map((c) => (
                            <SelectItem key={c} value={c}>
                              {c}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        placeholder={placeholderForField(rule.field)}
                        value={rule.value}
                        onChange={(e) => updateRule(group.id, rule.id, 'value', e.target.value)}
                      />
                    )}
                  </div>
                )}

                <IconButton variant="ghost" size="sm" aria-label="Remove condition" onClick={() => removeRule(group.id, rule.id)}>
                  <X size={15} />
                </IconButton>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10, marginLeft: 44 }}>
            <Button variant="ghost" size="sm" startIcon={<Plus size={14} />} onClick={() => addRule(group.id)}>
              AND condition
            </Button>
          </div>
        </div>
      ))}

      <div>
        <Button variant="secondary" size="sm" startIcon={<Plus size={14} />} onClick={addGroup}>
          OR group
        </Button>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vn-space-2)',
          padding: '8px 12px',
          borderRadius: 'var(--vn-radius-md)',
          background: 'var(--vn-bg-subtle)',
          border: '1px solid var(--vn-border-default)',
        }}
      >
        <Code size={14} color="var(--vn-fg-muted)" />
        <code style={{ fontSize: 12, color: toneColor('info', 'text'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {preview || 'Apply to all requests'}
        </code>
      </div>
    </div>
  );
}
